import { Component, OnInit, Output, EventEmitter, Input, ViewChild, SimpleChanges } from '@angular/core';
import { debounceTime, filter, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { DialogService, LanguageService } from 'src/app/services';
import { MediaObserver } from '@angular/flex-layout';
import { fromEvent } from 'rxjs';
import * as _ from 'lodash';

@Component({
  selector: 'esc-generic-dropdown',
  templateUrl: './generic-dropdown.component.html',
  styleUrls: ['./generic-dropdown.component.scss']
})
export class GenericDropdownComponent implements OnInit {
  @Input() loading;
  @Input() getMore;
  @Input() disabled;
  @Input() arrayName;
  @Input() isRequired;
  @Input() previousData;
  @Input() dropDownData;
  @Input() dropdownType;
  @Input() dropdownLabel;
  @Input() requiredError;
  @Input() doesntExistLabel;
  @Output() fetchData = new EventEmitter;
  @Output() optionSelected = new EventEmitter;
  @Output() clearDropDownData = new EventEmitter;

  @ViewChild('dropdownAC') dropdownACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) dropdownACTr: MatAutocompleteTrigger;

  form: FormGroup;
  public listData = [];
  private currentFilters;
  defaultOption = {
    id: "0",
    code: "0",
    description: ''
  };
  networkErrMsg = this.languageService.getTranslation("MSG_ERROR_NETWORK_CONNECTION", {});

  constructor(
    private fb: FormBuilder,
    public media: MediaObserver,
    private dialogService: DialogService,
    private languageService: LanguageService,
  ) { }

  ngOnInit() {
    this.currentFilters = _.cloneDeep(INITIAL_OPTIONS);
    this.initForm();
    this.detectFormChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if(this.form && _.get(changes, 'previousData') && changes.previousData.currentValue) {
      this.form.get('dropdown').setValue(_.get(changes, 'previousData.currentValue'), { emitEvent: false });
      this.currentFilters.web_filter.keyword = "";
    }

    if(this.form && _.get(changes, 'dropDownData') && changes.dropDownData.currentValue) {
      this.listData = this.listData.concat(_.get(changes, 'dropDownData.currentValue'));
    }

    if(this.form && _.get(changes, 'requiredError') && changes.requiredError.currentValue) {
      this.form.get('dropdown').markAsTouched();
    }
    
    if (this.form && _.get(changes, 'disabled') && changes.disabled.currentValue) {
      this.form.disable()
    }
    else if (this.form && _.get(changes, 'disabled') && !changes.disabled.currentValue) {
      this.form.enable()
    }
  }

  initForm() {
    this.form = this.fb.group({
      dropdown: [null]
    })

    if(this.isRequired) {
      this.form.get('dropdown').setValidators([
        Validators.required
      ])
    }

    if(_.get(this, 'previousData')) {
      let dropdownObj = {
        id: _.get(this, 'previousData.id') || 0,
        code: _.get(this, 'previousData.code') || "0",
        description: _.get(this, 'previousData.description') || _.get(this, 'previousData.containerNumber') || _.get(this, 'previousData.bookingRefNum') || _.get(this, 'previousData.controlNumber') || null,
      }
      this.form.get('dropdown').setValue(dropdownObj, { emitEvent: false });
    }

    if(this.disabled) {
      this.form.controls['dropdown'].disable();
    }
  }

  detectFormChanges() {
    this.form.get('dropdown').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      if (_.toLower(this.arrayName) == 'districts' && this.previousData === null) {
        return;
      }
      this.currentFilters.web_filter.keyword = _.toString(value);
      this.listData = [];
      this.getListData();
    });
  }

  getInitialValues() {
    this.currentFilters.web_filter.fromRow = 0;
    this.listData = [];
    this.getListData();
  }
   
  getMoreData() {
    if (this.getMore) {
      //this.currentFilters.web_filter.fromRow += INITIAL_OPTIONS.toRow;
      this.getListData();
    }
  }

  getListData() {
    if(this.currentFilters.web_filter.keyword && !this.getMore) {
      this.currentFilters.web_filter.fromRow = 0;
    }

    let requestParameters = {
      name: _.camelCase(_.get(this, 'dropdownType')),
      arrayName: this.arrayName,
      filters: _.cloneDeep(this.currentFilters)
    }

    this.fetchData.next(requestParameters);
  }

  displayFn(data?: any): string | undefined {
    if(_.has(data, 'code') && _.has(data, 'description')) {
      return _.get(data, 'description');
    }
    else if(_.has(data, 'containerNumber')) {
      return _.get(data, 'code') != "all" ? _.get(data, 'containerNumber') : _.get(data, 'description');
    }
    else if(_.has(data, 'bookingRefNum')) {
      return _.get(data, 'code') != "all" ? _.get(data, 'bookingRefNum') : _.get(data, 'description');
    }
    else if(_.has(data, 'controlNumber')) {
      return _.get(data, 'code') != "all" ? _.get(data, 'controlNumber') : _.get(data, 'description');
    }
    return null;
  }

  autoCmpltClosed() {
    this.listData = [];
    this.clearDropDownData.next(true);
  
    // use this if you need to filter return data
    if (_.isObject(_.get(this.form.get('dropdown'), 'value'))) {
      this.optionSelected.next(_.get(this.form.get('dropdown'), 'value'))
    }
  }

  clearForm(ev: Event, form, ctrl) {
    if(this.disabled){
      return;
    }
    form.get(ctrl).setValue(null, { emitEvent: false });
    this.currentFilters.web_filter.keyword = "";
    this.optionSelected.next(null)
  }

  autocompleteScroll(name, refName, acTrigger) {
    setTimeout(() => {
      if (this[refName].isOpen || this[refName].panel) {
        fromEvent(this[refName].panel.nativeElement, 'scroll')
          .pipe(
            map(x => this[refName].panel.nativeElement.scrollTop),
            takeUntil(this[acTrigger].panelClosingActions)
          )
          .subscribe(x => {
            const scrollTop = this[refName].panel.nativeElement
              .scrollTop;
            const scrollHeight = this[refName].panel.nativeElement
              .scrollHeight;
            const elementHeight = this[refName].panel.nativeElement
              .clientHeight;
            const atBottom = scrollHeight === scrollTop + elementHeight;
            if (atBottom) {
              // fetch more data
              this.getMoreData();
            }
          });
      }
    });
  }

  checkAutocompleteObject(form, ctrl) {
    switch (this.dropdownType) { 
      case "containerNumber": { 
        if ((!_.isObject(form.controls[ctrl].value) || form.controls[ctrl].value.containerNumber == null) && (form.controls[ctrl].value != null && form.controls[ctrl].value != "")) {
          form.controls[ctrl].setErrors({
            "notExisting": true
          });
          
          this.optionSelected.next(null)
        }
        break;
      }

      case "bookingRefNum": { 
        if ((!_.isObject(form.controls[ctrl].value) || form.controls[ctrl].value.bookingRefNum == null) && (form.controls[ctrl].value != null && form.controls[ctrl].value != "")) {
          form.controls[ctrl].setErrors({
            "notExisting": true
          });
          
          this.optionSelected.next(null)
        }
        break;
      }

      case "controlNumber": { 
        if ((!_.isObject(form.controls[ctrl].value) || form.controls[ctrl].value.controlNumber == null) && (form.controls[ctrl].value != null && form.controls[ctrl].value != "")) {
          form.controls[ctrl].setErrors({
            "notExisting": true
          });
          
          this.optionSelected.next(null)
        }
        break;
      }

      default : {
        if ((!_.isObject(form.controls[ctrl].value) || form.controls[ctrl].value.id == null) && (form.controls[ctrl].value != null && form.controls[ctrl].value != "")) {
          form.controls[ctrl].setErrors({
            "notExisting": true
          });
          
          this.optionSelected.next(null)
        }
        break;
      }
    }
  }
}

const INITIAL_OPTIONS = {
  web_filter: {
    offset: 0,
    limit: 50,
    keyword: "",
    status: null,
    getMore: true
  }
}